import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';

import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/Utils';
import { useHrAnalyticsApi, RULE_NAME_KEY } from './config';
import AlertEvidenceDrawer, { SEVERITY_META, parseAlertEvidence } from './components/AlertEvidenceDrawer';

// Görev 10 — Erken Uyarılar sayfası (spec §13.3). 2026-08-03 (kullanıcı isteği): kart sarmalı
// kaldırıldı — sayfa yalnız standart DataTable'dır ve sayfa başlığı tablonun kendi title'ıdır
// (EmployeesPage tarzı liste sayfası düzeni). Liste standart DataTable (hand-rolled tablo
// YASAK, CLAUDE.md).
//
// HaAlertsController.GetAll'ın verdiği sayfalanmış listeyi FİLTRESİZ çeker: HaAlert küçük,
// upsert ile tekilleştirilmiş bir tablodur (kural × kapsam × kişi ile sınırlı — çalışan
// sayısıyla DEĞİL, bkz. HaAlert.cs sınıf özeti), bu yüzden HaAlertsController.MaxPageSize
// (200) tek istekte "tamamına yakın"ı getirir; Overview/MyShiftSwaps sayfalarının AYNI
// `serverSide={false}` + client-side DataTable sayfalama/sıralama deseni izlenir.
const PAGE_SIZE = 200;

// Kullanıcı-tetikli geçiş matrisi — HaAlertsDataAccess.AllowedTransitions ile BİREBİR AYNI
// (bkz. o dosyanın yorumu): open → {acknowledged, resolved, dismissed}; acknowledged →
// {resolved, dismissed}; resolved/dismissed hiçbir KAYNAK kümesinde YOK (kullanıcı açısından
// donmuş uç durumlar). Burada TEKRAR kodlanıyor çünkü FE hangi butonların GÖRÜNECEĞİNE karar
// vermek zorunda — sunucu geçersiz bir PATCH'i zaten 400 (ha.alert.invalidTransition) ile
// reddeder, ama önce göstermemek daha iyi bir kullanıcı deneyimidir.
const ALLOWED_TRANSITIONS = {
  open: ['acknowledged', 'resolved', 'dismissed'],
  acknowledged: ['resolved', 'dismissed'],
};

function availableTargets(status) {
  return ALLOWED_TRANSITIONS[status] || [];
}

// Her hedef durumun buton etiketi + onay diyaloğu metni. "resolved" onayı BİLEREK bir ipucu
// taşır — brief'in Görev 9 incelemesinden taşınan ürün detayı: gecelik worker bulgu tekrar
// oluşursa çözülmüş bir uyarıyı OTOMATİK olarak open'a döndürür (HaAlertWriter). Bu ipucu
// olmadan kullanıcı "çöz"ü "bir daha asla gösterme" sanır ve uyarı geri döndüğünde güven
// kaybeder. "Yoksay" (dismissed) GERÇEKTEN kalıcıdır (absorbing) — bu yüzden onun mesajı bunun
// TERSİNİ, kalıcılığı doğrular.
const ACTION_CONFIG = {
  acknowledged: {
    labelKey: 'hrAnalytics.alerts.actions.acknowledge',
    titleKey: 'hrAnalytics.alerts.confirm.acknowledgeTitle',
    messageKey: 'hrAnalytics.alerts.confirm.acknowledgeMessage',
  },
  resolved: {
    labelKey: 'hrAnalytics.alerts.actions.resolve',
    titleKey: 'hrAnalytics.alerts.confirm.resolveTitle',
    messageKey: 'hrAnalytics.alerts.confirm.resolveMessage',
  },
  dismissed: {
    labelKey: 'hrAnalytics.alerts.actions.dismiss',
    titleKey: 'hrAnalytics.alerts.confirm.dismissTitle',
    messageKey: 'hrAnalytics.alerts.confirm.dismissMessage',
  },
};

const STATUS_TONE = {
  open: 'warning',
  acknowledged: 'info',
  resolved: 'success',
  dismissed: 'muted',
};

function toneColor(theme, tone) {
  if (tone === 'success') return theme.colors.auxiliar.success;
  if (tone === 'warning') return theme.colors.auxiliar.warning;
  if (tone === 'info') return theme.colors.auxiliar.info;
  return theme.colors.text.muted;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;


// Brief: "boş listede açıklayıcı durum gösterir, boş tablo iskeleti göstermez" — bu bir hata
// veya "veri yok" durumu değil, "her şey yolunda" durumudur (açık uyarı bulunmuyor), bu yüzden
// nötr bir mesaj yerine olumlu bir simge kullanılır.
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 16px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  color: ${({ theme }) => theme.colors.auxiliar.success};
  margin-bottom: 4px;
`;

const EmptyTitle = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EmptyDescription = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  max-width: 420px;
`;

// Şiddet rengin YANINDA ikon + metinle gösterilir — renk TEK BAŞINA değil (FunnelPlot.js'in
// dosya başı yorumunda belgelenen, incelemeden geçmiş AYNI dataviz ilkesi).
const SeverityBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ $color }) => `${$color}22`};
  color: ${({ $color }) => $color};
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ $color }) => `${$color}22`};
  color: ${({ $color }) => $color};
  white-space: nowrap;
`;

const ActionsCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ActionButton = styled(Button)`
  padding: 5px 12px !important;
  font-size: ${({ theme }) => theme.metrics.font.size.xs} !important;
`;

export default function HrAnalyticsAlertsPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { canWrite } = useAuth();
  const { getAlerts, patchAlert } = useHrAnalyticsApi();

  // Yalnızca HA.Write yetkisi olan kullanıcıya eylem butonları gösterilir —
  // OffboardingItAccessPage.js'in `writable` deseniyle AYNI savunma: yalnız-okuma yetkisi olan
  // bir kullanıcıya sunucunun 403 ile reddedeceği butonlar göstermek yanıltıcıdır.
  const writable = canWrite ? canWrite('HA') : false;

  const [alerts, setAlerts] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [confirmState, setConfirmState] = useState(null); // { id, target } | null
  const [processing, setProcessing] = useState(false);
  const [evidenceAlert, setEvidenceAlert] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    // Hata toast'ı YOK (CLAUDE.md) — hatalar yalnız Axios interceptor'da ele alınır. Filtre
    // parametresi BİLEREK YOK: bu ekran tenant'ın tüm uyarı durumlarını (open/acknowledged/
    // resolved/dismissed) gösterir — HaAlert zaten küçük, upsert ile tekilleştirilmiş bir
    // tablodur (bkz. dosya başı yorumu), gizli bir varsayılan filtre İCAT EDİLMEDİ.
    getAlerts({ pageNumber: 1, pageSize: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        const paginatedList = res?.data?.data;
        setAlerts(Array.isArray(paginatedList?.data) ? paginatedList.data : []);
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });
    return () => { cancelled = true; };
  }, [getAlerts, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const openConfirm = useCallback((id, target) => setConfirmState({ id, target }), []);
  const closeConfirm = useCallback(() => setConfirmState(null), []);

  // No try/catch here on purpose — MyShiftSwapsPage.js'teki AYNI gerekçe: Axios interceptor
  // zaten mutasyon hatalarını toast'lar ve promise'i reddeder; bir ret burada yalnızca
  // aşağıdaki .then()'in hiç çalışmaması (liste tazelenmez) anlamına gelir. Bileşende toast/
  // catch YOK (global kısıt) — `finally` yalnızca yerel dialog/işlem durumunu temizler.
  const handleConfirm = useCallback(() => {
    if (!confirmState) return;
    setProcessing(true);
    patchAlert(confirmState.id, { status: confirmState.target })
      .then(() => refresh())
      .finally(() => {
        setProcessing(false);
        setConfirmState(null);
      });
  }, [confirmState, patchAlert, refresh]);

  const openEvidence = useCallback((row) => setEvidenceAlert(row), []);
  const closeEvidence = useCallback(() => setEvidenceAlert(null), []);

  // 2026-07-31, tüm-dal incelemesi (Important, I1): tablo `serverSide={false}` (aşağıda) —
  // mui-datatables kullanıcı bir sütun başlığına tıkladığında KENDİ dahili görüntü kopyasını
  // yeniden sıralar; `tableMeta.rowIndex` bu SIRALANMIŞ diziideki konumdur, `alerts` state'indeki
  // orijinal konum DEĞİLDİR. `alerts[tableMeta.rowIndex]` bu yüzden sıralamadan SONRA YANLIŞ
  // satırı çözer — "Yoksay" butonu ekranda GÖRÜNEN uyarıyı değil BAŞKA birini dismiss edebilir
  // (dismissed KALICIDIR, kullanıcı geri alamaz). Düzeltme: `tableMeta.currentTableData` mui-
  // datatables'ın sıralama/sayfalama SONRASI görüntü dizisidir; her elemanın `.index`'i ORİJİNAL
  // `alerts` dizisindeki konumu taşır — paylaşılan DataTable sarmalayıcısının kendi `onRowClick`'i
  // (aşağıda) zaten `rowMeta.dataIndex` ile AYNI ilkeyi uyguluyor (bkz. DataTable.js).
  const columns = useMemo(() => {
    const base = [
      {
        name: 'severity',
        label: t('hrAnalytics.alerts.columns.severity'),
        options: {
          filter: true,
          sort: true,
          filterType: 'dropdown',
          filterOptions: {
            names: ['red', 'amber'],
            renderValue: (v) => t(`hrAnalytics.alerts.severities.${v}`),
          },
          customFilterListOptions: { render: (v) => t(`hrAnalytics.alerts.severities.${v}`) },
          customBodyRender: (value) => {
            const meta = SEVERITY_META[value] || SEVERITY_META.amber;
            const Icon = meta.Icon;
            const color = theme.colors.auxiliar[meta.colorKey];
            return (
              <SeverityBadge $color={color}>
                <Icon size={14} aria-hidden="true" />
                {t(meta.labelKey)}
              </SeverityBadge>
            );
          },
        },
      },
      {
        name: 'ruleCode',
        label: t('hrAnalytics.alerts.columns.rule'),
        options: {
          filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' },
          // Sütun HAM KOD gösteriyordu (`training.mandatoryOverdue` gibi) — kullanıcı için
          // okunamaz. Çeviriler zaten beş dilde MEVCUTTU, yalnız burada kullanılmıyordu.
          // Eşleme bilinmiyorsa kod olduğu gibi kalır: yanlış bir ad uydurmaktan iyidir.
          customBodyRender: (value) => {
            const key = RULE_NAME_KEY[value];
            return key ? t(`hrAnalytics.settings.rules.names.${key}`) : value;
          },
        },
      },
      {
        name: 'scopeType',
        label: t('hrAnalytics.alerts.columns.scope'),
        options: {
          filter: true,
          sort: true,
          filterType: 'dropdown',
          filterOptions: {
            names: ['tenant', 'location', 'position_family', 'department'],
            renderValue: (v) => t(`hrAnalytics.scopes.${v}`),
          },
          customFilterListOptions: { render: (v) => t(`hrAnalytics.scopes.${v}`) },
          customBodyRender: (value, tableMeta) => {
            const row = alerts[tableMeta.currentTableData[tableMeta.rowIndex].index];
            if (!row) return null;
            const label = t(`hrAnalytics.scopes.${row.scopeType}`);
            // FunnelPlot.js'teki AYNI "kapsam etiketi + kısa id" deseni — organizasyon
            // birimi kimliği (departman/lokasyon Guid'i) bir KİŞİ kimliği DEĞİLDİR, gizlilik
            // kapsamı dışındadır (bkz. HaAlertsDataAccess sınıf özeti — maskelenen yalnız
            // SubjectUserId'dir).
            if (!row.scopeId) return label;
            return `${label} #${String(row.scopeId).slice(0, 8)}`;
          },
        },
      },
      {
        // Sanal sütun — OffboardingItAccessPage.js'in 'actions' sütunu gibi ham veride
        // KARŞILIĞI yok, tamamen customBodyRender'dan üretilir (bkz. aşağısı).
        name: 'affected',
        label: t('hrAnalytics.alerts.columns.affected'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (_, tableMeta) => {
            const row = alerts[tableMeta.currentTableData[tableMeta.rowIndex].index];
            if (!row) return null;
            // Task 10 follow-up (koordinatör + Task 10 incelemesi, 2026-07-30): kimlik taşıyan
            // (SubjectUserId dolu) bir satırda backend'in çözdüğü adı (SubjectDisplayName)
            // gösteririz — bayrak (IndividualScoringEnabled) opt-in edilmişse KVKK dengesi bunu
            // haklı kılıyor demektir; her satırı "1 çalışan" göstermek (eski davranış) bayrağı
            // GÖZLEMLENEBİLİR hiçbir şey değiştirmeyen bir törene indirgerdi (bkz. backend
            // HaAlertsDataAccess sınıf özeti "SubjectDisplayName"). Ad çözülemezse (ör. bağlı bir
            // Employee yok — nadir, login-only hesap) "1 çalışan" biçimine düşülür — HAM id ASLA
            // gösterilmez. Toplanmış (SubjectUserId null + EvidenceJson.affectedCount) satırda
            // "N çalışan". İkisi de yoksa (bireysel-olmayan bir kural — ör. talent.successionGap/
            // onboarding.earlyLeaveRisk) bu sütunun kavramı yok, tire gösterilir.
            if (row.subjectUserId) {
              return row.subjectDisplayName || t('hrAnalytics.alerts.affectedCount', { count: 1 });
            }
            const evidence = parseAlertEvidence(row.evidenceJson);
            if (typeof evidence.affectedCount === 'number') {
              return t('hrAnalytics.alerts.affectedCount', { count: evidence.affectedCount });
            }
            return '—';
          },
        },
      },
      {
        name: 'triggeredAt',
        label: t('hrAnalytics.alerts.columns.triggeredAt'),
        options: {
          filter: false, sort: true,
          customBodyRender: (value) => formatDate(value, i18n.language),
        },
      },
      {
        name: 'status',
        label: t('hrAnalytics.alerts.columns.status'),
        options: {
          filter: true,
          sort: true,
          filterType: 'dropdown',
          filterOptions: {
            names: ['open', 'acknowledged', 'resolved', 'dismissed'],
            renderValue: (v) => t(`hrAnalytics.alerts.statuses.${v}`),
          },
          customFilterListOptions: { render: (v) => t(`hrAnalytics.alerts.statuses.${v}`) },
          customBodyRender: (value) => (
            <StatusBadge $color={toneColor(theme, STATUS_TONE[value] || 'muted')}>
              {t(`hrAnalytics.alerts.statuses.${value}`)}
            </StatusBadge>
          ),
        },
      },
    ];

    if (!writable) return base;

    return [
      ...base,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = alerts[tableMeta.currentTableData[tableMeta.rowIndex].index];
            if (!row) return null;
            const targets = availableTargets(row.status);
            if (targets.length === 0) return null;
            return (
              <ActionsCell onClick={(e) => e.stopPropagation()}>
                {targets.map((target) => (
                  <ActionButton
                    key={target}
                    type="button"
                    variant="ghost"
                    data-action={target}
                    onClick={() => openConfirm(row.id, target)}
                  >
                    {t(ACTION_CONFIG[target].labelKey)}
                  </ActionButton>
                ))}
              </ActionsCell>
            );
          },
        },
      },
    ];
  }, [t, i18n.language, alerts, theme, writable, openConfirm]);

  const isEmpty = !fetching && alerts.length === 0;

  return (
    <Container>
      {/* Kart sarmalayıcısı kaldırıldı (kullanıcı isteği 2026-08-03): sayfa yalnız DataTable,
          sayfa başlığı tablonun kendi title'ı. "Her şey yolunda" boş durumu KORUNDU — bilinçli,
          incelemeden geçmiş bir tasarım (boş tablo iskeleti yerine olumlu durum) ve yalnız hiç
          uyarı yokken görünür. */}
      {isEmpty ? (
        <EmptyState data-role="ha-alerts-empty">
          <EmptyIcon><ShieldCheck size={40} aria-hidden="true" /></EmptyIcon>
          <EmptyTitle>{t('hrAnalytics.alerts.empty.title')}</EmptyTitle>
          <EmptyDescription>{t('hrAnalytics.alerts.empty.description')}</EmptyDescription>
        </EmptyState>
      ) : (
        <DataTable
          title={t('hrAnalytics.alerts.pageTitle')}
          serverSide={false}
          fetching={fetching}
          columns={columns}
          data={alerts}
          pageSize={10}
          onRowClick={openEvidence}
        />
      )}

      <AlertEvidenceDrawer isOpen={!!evidenceAlert} alert={evidenceAlert} onClose={closeEvidence} />

      <div data-role="alert-confirm-dialog">
        <ConfirmDialog
          isOpen={!!confirmState}
          title={confirmState ? t(ACTION_CONFIG[confirmState.target].titleKey) : ''}
          message={confirmState ? t(ACTION_CONFIG[confirmState.target].messageKey) : ''}
          onConfirm={handleConfirm}
          onCancel={closeConfirm}
          loading={processing}
        />
      </div>
    </Container>
  );
}
